import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button, Badge } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SessionTimes from '../../components/Mentors/SessionTimes';
import InviteLinkBlock from './InviteLinkBlock';
import TeamScopeFilter from './TeamScopeFilter';
import {
  LAUNCH_STEP_META,
  LAUNCH_TYPE_LABELS,
  LAUNCH_STATUS_LABELS,
  isPipelineParticipant,
  isPipelineLeadership,
  defaultTeamScope,
  canEditLaunchForAssociate,
  pickActiveLaunch,
} from './pipelineUtils';

const { FiCheck, FiArrowLeft, FiMonitor } = FiIcons;

const WAITING_COPY = 'Waiting on your manager to schedule this launch\'s event';

function PresentationToolLaunchCard() {
  return (
    <Card
      className="p-5 sm:p-6 border border-[#d9ad5b]/35"
      style={{
        background: 'linear-gradient(140deg, #151619 0%, #0b0c0e 70%)',
        boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
      }}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex gap-3.5 items-start">
          <span
            className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-[#d9ad5b]"
            style={{borderColor: 'rgba(217,173,91,0.55)'}}
            aria-hidden
          >
            <SafeIcon icon={FiMonitor} className="h-4 w-4" />
          </span>
          <div>
            <p
              className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] mb-1"
              style={{color: '#d9ad5b'}}
            >
              Live event tool
            </p>
            <h2 className="text-[15.5px] font-heading font-extrabold text-[#f4efe4]">
              Prosperity Launch Countdown & Presentation
            </h2>
            <p className="text-sm mt-1.5 max-w-xl" style={{color: '#9b998f'}}>
              Full-screen countdown, reveal, and PDF presentation for Zoom Virtual Business Launch screen-shares.
            </p>
          </div>
        </div>
        <a
          href="/launch-toolkit/presentation"
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-[10px] px-4 py-2.5 text-sm font-bold text-[#17140f] transition hover:brightness-110"
          style={{background: '#d9ad5b'}}
        >
          Launch
        </a>
      </div>
    </Card>
  );
}

function LaunchChecklistPanel({
  launch,
  invite,
  inviteUserId,
  canEdit,
  savingId,
  onMarkStepDone,
  onInviteGenerated,
  viewingLabel = null,
}) {
  const steps = (launch?.launch_steps || []).slice().sort(
    (a, b) => (a.step_number ?? 0) - (b.step_number ?? 0),
  );
  const pmEvent = launch?.pm_event || (launch?.pm_event_id ? { id: launch.pm_event_id } : null);
  const eventLinked = Boolean(launch?.pm_event_id);
  const eventSession = (pmEvent?.sessions || [])[0];

  return (
    <div className="space-y-6">
      {viewingLabel && (
        <div className="rounded-[14px] border border-primary/30 bg-skyTint/40 px-4 py-3">
          <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-0.5">
            Viewing as leadership
          </p>
          <p className="text-sm font-bold text-navy">{viewingLabel}</p>
          {!canEdit && (
            <p className="text-xs text-slateText mt-1">Read-only — you can view this launch but not edit steps.</p>
          )}
        </div>
      )}

      <Card className="p-6">
        <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-1">Launch Sequence</p>
        <h2 className="text-[15.5px] font-heading font-extrabold text-navy mb-5">Where You Are</h2>
        <div className="flex gap-0 overflow-x-auto pb-1">
          {LAUNCH_STEP_META.map((meta, i) => {
            const row = steps.find((s) => s.step_type === meta.step_type);
            const status = row?.status || 'upcoming';
            return (
              <div key={meta.step_type} className="flex-1 min-w-[140px] px-1.5 relative">
                {i < LAUNCH_STEP_META.length - 1 && (
                  <div
                    className={`absolute top-[17px] right-[-6px] h-0.5 w-[calc(100%-12px)] ${
                      status === 'done' ? 'bg-primary' : 'bg-lineGray'
                    }`}
                  />
                )}
                <div
                  className={`relative z-[1] w-[34px] h-[34px] rounded-full border-2 flex items-center justify-center text-[13px] font-extrabold ${
                    status === 'done'
                      ? 'bg-primary border-primary text-white'
                      : status === 'current'
                        ? 'bg-amber border-amber text-white'
                        : 'bg-white border-lineGray text-slateText'
                  }`}
                >
                  {status === 'done' ? <SafeIcon icon={FiCheck} className="h-3.5 w-3.5" /> : meta.step_number}
                </div>
                <p className="text-[11.8px] font-bold text-navy mt-2">{meta.label}</p>
                <p className="text-[10.5px] text-slateText mt-0.5 leading-snug max-w-[135px]">{meta.description}</p>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="space-y-3.5">
        {LAUNCH_STEP_META.map((meta) => {
          const row = steps.find((s) => s.step_type === meta.step_type);
          const status = row?.status || 'upcoming';
          const isCurrent = status === 'current';
          const needsEvent = meta.step_type === 'crm_invite' || meta.step_type === 'event_day';
          const blocked = needsEvent && !eventLinked;

          return (
            <div
              key={meta.step_type}
              className={`rounded-[14px] border p-5 flex gap-4 items-start ${
                blocked
                  ? 'border-lineGray bg-ivory'
                  : isCurrent
                    ? 'border-amber bg-[#fdf8f0]'
                    : 'border-lineGray bg-white'
              }`}
            >
              <div className="w-[30px] h-[30px] rounded-lg bg-navy text-white font-extrabold text-[12.5px] flex items-center justify-center shrink-0">
                {meta.step_number}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-bold text-navy">{meta.title}</h3>
                  {blocked && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-[100px] bg-lockedBg text-locked">Waiting</span>
                  )}
                  {!blocked && status === 'done' && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-[100px] bg-successBg text-success">Done</span>
                  )}
                  {!blocked && isCurrent && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-[100px] bg-warningBg text-warning">Up Next</span>
                  )}
                </div>
                <p className="text-[12.2px] text-slateText mt-1 leading-relaxed">{meta.description}</p>

                {blocked && (
                  <p className="text-sm font-semibold text-slateText mt-3">{WAITING_COPY}</p>
                )}

                {!blocked && meta.step_type === 'crm_invite' && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slateText mb-2">Invite Link</p>
                    <InviteLinkBlock
                      event={pmEvent}
                      userId={inviteUserId}
                      invite={invite}
                      onGenerated={onInviteGenerated}
                    />
                  </div>
                )}

                {!blocked && meta.step_type === 'event_day' && pmEvent && (
                  <div className="mt-3 rounded-xl border border-lineGray bg-white px-4 py-3 space-y-1">
                    <p className="text-sm font-bold text-navy">{pmEvent.title}</p>
                    {eventSession?.starts_at && (
                      <SessionTimes
                        startsAt={eventSession.starts_at}
                        endsAt={eventSession.ends_at}
                        timezone={eventSession.timezone}
                      />
                    )}
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Badge variant={pmEvent.status === 'published' ? 'success' : 'warning'}>
                        {pmEvent.status || 'draft'}
                      </Badge>
                      {pmEvent.slug && (
                        <span className="text-xs font-mono text-slateText">/{pmEvent.slug}</span>
                      )}
                    </div>
                  </div>
                )}

                {canEdit && !blocked && isCurrent && (
                  <Button
                    size="sm"
                    className="mt-3"
                    isLoading={savingId === row?.id}
                    disabled={!row?.id || savingId === row?.id}
                    onClick={() => onMarkStepDone(row)}
                  >
                    {meta.action}
                  </Button>
                )}

                {canEdit && !blocked && status === 'done' && meta.step_type !== 'crm_invite' && meta.step_type !== 'event_day' && (
                  <Button size="sm" variant="secondary" className="mt-3" disabled>
                    {meta.doneAction}
                  </Button>
                )}

                {canEdit && !blocked && status === 'upcoming' && (
                  <Button size="sm" variant="outline" className="mt-3" disabled>
                    Locked until previous step is done
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssociateLaunchToolkit({ user, addToast }) {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [launch, setLaunch] = useState(null);
  const [invite, setInvite] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let rows = await api.businessLaunches.getAllForAssociate(user.id);
      let active = pickActiveLaunch(rows);
      if (!active) {
        active = await api.businessLaunches.create({
          associateId: user.id,
          launchType: 'personal_start',
        });
      }
      active = await api.businessLaunches.getById(active.id);
      setLaunch(active);
      if (active?.pm_event?.id) {
        const link = await api.pipelineEvents.getInvitationLink(active.pm_event.id, user.id);
        setInvite(link ?? null);
      } else {
        setInvite(null);
      }
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Could not load launch', message: err.message || 'Try again.' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const markStepDone = async (step) => {
    if (!step?.id || step.status !== 'current') return;
    setSavingId(step.id);
    try {
      await api.launchSteps.updateStatus(step.id, 'done');
      const refreshed = await api.businessLaunches.getById(launch.id);
      setLaunch(refreshed);
      addToast({ type: 'success', title: 'Step updated', message: 'Marked complete. Next step is now current.' });
    } catch (err) {
      addToast({ type: 'error', title: 'Update failed', message: err.message || 'Try again.' });
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
              Launch <span className="text-primary">Toolkit</span>
            </h1>
            <p className="text-slateText text-sm mt-1">
              Work the 5-step sequence for your active Business Launch.
            </p>
          </div>
          <div className="self-start rounded-[100px] border border-lineGray bg-white px-3.5 py-2 text-xs font-semibold text-slateText">
            System 1 · Launch
          </div>
        </div>
        <PresentationToolLaunchCard />
        <div className="flex items-center justify-center h-64 gap-3">
          <Spinner size="lg" className="text-primary" />
          <p className="text-slateText text-sm">Loading launch toolkit…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
            Launch <span className="text-primary">Toolkit</span>
          </h1>
          <p className="text-slateText text-sm mt-1">
            Work the 5-step sequence for your active Business Launch.
          </p>
        </div>
        <div className="self-start rounded-[100px] border border-lineGray bg-white px-3.5 py-2 text-xs font-semibold text-slateText">
          System 1 · Launch
        </div>
      </div>

      <PresentationToolLaunchCard />

      <LaunchChecklistPanel
        launch={launch}
        invite={invite}
        inviteUserId={user.id}
        canEdit
        savingId={savingId}
        onMarkStepDone={markStepDone}
        onInviteGenerated={setInvite}
      />
    </div>
  );
}

function LeadershipLaunchToolkit({ user, addToast }) {
  const [scope, setScope] = useState(() => defaultTeamScope(user?.role));
  const [team, setTeam] = useState([]);
  const [launches, setLaunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [detailLaunch, setDetailLaunch] = useState(null);
  const [detailInvite, setDetailInvite] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const teamIds = useMemo(() => team.map((u) => u.id), [team]);

  const loadList = useCallback(async () => {
    if (!user?.agency_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const myTeam = await api.users.getMyTeam();
      setTeam(myTeam || []);
      const associateIds = scope === 'my_team' ? (myTeam || []).map((u) => u.id) : null;
      const rows = await api.businessLaunches.listForAgency({
        agencyId: user.agency_id,
        associateIds,
      });
      setLaunches(rows || []);
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Could not load launches', message: err.message || 'Try again.' });
      setLaunches([]);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, scope, addToast]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openDetail = async (launchId) => {
    setDetailId(launchId);
    setDetailLoading(true);
    setDetailLaunch(null);
    setDetailInvite(null);
    try {
      const full = await api.businessLaunches.getById(launchId);
      setDetailLaunch(full);
      const associateId = full?.associate_id;
      if (full?.pm_event?.id && associateId) {
        const link = await api.pipelineEvents.getInvitationLink(full.pm_event.id, associateId);
        setDetailInvite(link ?? null);
      }
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Could not open launch', message: err.message || 'Try again.' });
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const markStepDone = async (step) => {
    if (!step?.id || step.status !== 'current' || !detailLaunch?.id) return;
    setSavingId(step.id);
    try {
      await api.launchSteps.updateStatus(step.id, 'done');
      const refreshed = await api.businessLaunches.getById(detailLaunch.id);
      setDetailLaunch(refreshed);
      addToast({ type: 'success', title: 'Step updated', message: 'Marked complete. Next step is now current.' });
      await loadList();
    } catch (err) {
      addToast({ type: 'error', title: 'Update failed', message: err.message || 'Try again.' });
    } finally {
      setSavingId(null);
    }
  };

  if (detailId) {
    const associateName =
      detailLaunch?.associate?.full_name
      || launches.find((l) => l.id === detailId)?.associate?.full_name
      || 'Associate';
    const canEdit = canEditLaunchForAssociate(
      user?.role,
      detailLaunch?.associate_id,
      user?.id,
      teamIds,
    );

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => {
                setDetailId(null);
                setDetailLaunch(null);
                setDetailInvite(null);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline mb-2"
            >
              <SafeIcon icon={FiArrowLeft} className="h-3.5 w-3.5" />
              Back to launches
            </button>
            <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
              Launch <span className="text-primary">Toolkit</span>
            </h1>
          </div>
        </div>

        <PresentationToolLaunchCard />

        {detailLoading || !detailLaunch ? (
          <div className="flex items-center justify-center h-48 gap-3">
            <Spinner size="lg" className="text-primary" />
            <p className="text-slateText text-sm">Loading launch…</p>
          </div>
        ) : (
          <LaunchChecklistPanel
            launch={detailLaunch}
            invite={detailInvite}
            inviteUserId={detailLaunch.associate_id}
            canEdit={canEdit}
            savingId={savingId}
            onMarkStepDone={markStepDone}
            onInviteGenerated={setDetailInvite}
            viewingLabel={`Viewing ${associateName}'s launch`}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
            Launch <span className="text-primary">Toolkit</span>
          </h1>
          <p className="text-slateText text-sm mt-1">
            Review and coach Business Launches across the agency.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TeamScopeFilter value={scope} onChange={setScope} />
          <div className="rounded-[100px] border border-lineGray bg-white px-3.5 py-2 text-xs font-semibold text-slateText">
            System 1 · Launch
          </div>
        </div>
      </div>

      <PresentationToolLaunchCard />

      {loading ? (
        <div className="flex items-center justify-center h-48 gap-3">
          <Spinner size="lg" className="text-primary" />
          <p className="text-slateText text-sm">Loading launches…</p>
        </div>
      ) : launches.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-slateText">
            {scope === 'my_team'
              ? 'No launches for your team yet. Assign associates as trainees or under your marketing director role, or switch to All.'
              : 'No business launches in the agency yet.'}
          </p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-lineGray bg-ivory/60">
                  <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold px-5 py-3">Associate</th>
                  <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold px-5 py-3">Launch type</th>
                  <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold px-5 py-3">Status</th>
                  <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {launches.map((row) => (
                  <tr key={row.id} className="border-b border-[#F0EEE7] last:border-0 hover:bg-skyTint/20">
                    <td className="px-5 py-3.5 text-[13px] font-bold text-navy">
                      {row.associate?.full_name || '—'}
                    </td>
                    <td className="px-5 py-3.5 text-[12.5px] text-slateText">
                      {LAUNCH_TYPE_LABELS[row.launch_type] || row.launch_type}
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge
                        variant={
                          row.status === 'completed'
                            ? 'success'
                            : row.status === 'scheduled'
                              ? 'warning'
                              : 'default'
                        }
                      >
                        {LAUNCH_STATUS_LABELS[row.status] || row.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(row.id)}>
                        Open checklist
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default function LaunchToolkit() {
  const { user } = useAuth();
  const { addToast } = useToast();

  if (isPipelineParticipant(user?.role)) {
    return <AssociateLaunchToolkit user={user} addToast={addToast} />;
  }
  if (isPipelineLeadership(user?.role)) {
    return <LeadershipLaunchToolkit user={user} addToast={addToast} />;
  }

  return (
    <Card className="p-8 max-w-xl">
      <h1 className="text-xl font-heading font-bold text-navy">Launch Toolkit</h1>
      <p className="text-sm text-slateText mt-3">This page is available to pipeline participants and leadership.</p>
    </Card>
  );
}
